import { set, upd } from "../common/util/Immutable";
import uuidv7 from "../common/util/uuidv7";
import {
  CHILD_TYPES,
  createNode,
  NUM_REQUIRED_DESCENDANTS,
  TAnyNodeID,
  TAppNode,
  TNodeID,
  TNodeType,
  TNodeUnion,
  TPoint,
  TScene,
} from "./_generated/NodeTypes";
import { createFracList, insert, TPosition } from "./util/FracList";

export type TControlID = string;
export type TWireID = string;

type TKeysOfValue<T, V> = {
  [K in keyof T]: T[K] extends V ? K : never;
}[keyof T];

type TNodeIDToNodeType<T> = T extends TNodeID<infer U> ? U : never;

type TNodeProps<NodeType extends TNodeType> = Extract<
  TNodeUnion,
  { type: NodeType; props: any }
>["props"];

function createNodeId<NodeType extends TNodeType>(): TNodeID<NodeType> {
  return crypto.randomUUID() as TNodeID<NodeType>;
}

BigInt.prototype.toJSON = function () {
  return { $bigint: this.toString() };
};

Map.prototype.toJSON = function () {
  return Object.fromEntries(this.entries());
};

export function createScene(): TScene {
  return {
    version: BigInt(-1),
    nodes: {
      ["0"]: {
        type: "app",
        children: {
          surfaces: createFracList(),
          states: {},
          events: {},
        },
      },
    } as { [x: TNodeID<"app">]: TAppNode },
    positions: {},
  };
}

export function newIds(nodeType: TNodeType) {
  return new Array(NUM_REQUIRED_DESCENDANTS[nodeType])
    .fill(0)
    .map(() => uuidv7());
}

export function addListChild<
  NodeType extends TNodeType,
  ChildKey extends TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "list">,
  ChildType extends TNodeIDToNodeType<
    Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey][number]
  >
>(
  scene: TScene,
  id: TNodeID<NodeType>,
  childKey: ChildKey,
  // position: number,
  position: TPosition,
  childType: ChildType,
  newIds: TNodeDescendantIDList<NodeType>
  // childId: TNodeID<ChildType> = createNodeId(),
  // childProps: TNodeProps<ChildType> = defaultProps(childType)
) {
  console.log(scene, id, childKey, position, childType, newIds);
  return upd(
    scene,
    "nodes",
    (v) =>
      upd(v, id, (v) =>
        upd(v, "children", (v) =>
          upd(v, childKey, (v) =>
            // [...v]
            insert(v, position, newIds[0])
          )
        )
      )
    // v
  );
}

type TAddListChildAction<
  NodeType extends TNodeType,
  ChildKey extends TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "list">,
  ChildType extends TNodeIDToNodeType<
    Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey][number]
  >
> = {
  action: "add_list_child";
  id: TNodeID<NodeType>;
  childKey: ChildKey;
  // position: number;
  position: TPosition;
  childType: ChildType;
  newIds: TNodeDescendantIDList<NodeType>;
};

export type TAddListChildActionUnion = {
  [NodeType in TNodeType]: {
    [ChildKey in TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "list">]: {
      [ChildType in TNodeIDToNodeType<
        Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey][number]
      >]: TAddListChildAction<NodeType, ChildKey, ChildType>;
    }[TNodeIDToNodeType<
      Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey][number]
    >];
  }[TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "list">];
}[TNodeType];

export function addGridSetChild<
  NodeType extends TNodeType,
  ChildKey extends TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "set">,
  ChildType extends TNodeIDToNodeType<
    keyof Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey]
  >
>(
  scene: TScene,
  id: TNodeID<NodeType>,
  childKey: ChildKey,
  position: TPoint,
  childType: ChildType,
  newIds: TNodeDescendantIDList<NodeType>
) {
  scene = createNode(scene, childType, newIds);
  scene = upd(scene, "nodes", (v) =>
    upd(v, id, (v) =>
      upd(v, "children", (v) =>
        upd(v, childKey, (v) => ({ ...v, [newIds[0]]: true }))
      )
    )
  );
  return upd(scene, "positions", (v) => set(v, newIds[0], position));
}

type TAddGridSetChildAction<
  NodeType extends TNodeType,
  ChildKey extends TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "set">,
  ChildType extends TNodeIDToNodeType<
    keyof Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey]
  >
> = {
  scene: TScene;
  id: TNodeID<NodeType>;
  childKey: ChildKey;
  position: TPoint;
  childType: ChildType;
  newIds: TNodeDescendantIDList<NodeType>;
};

export type TAddGridSetChildActionUnion = {
  [NodeType in TNodeType]: {
    [ChildKey in TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "set">]: {
      [ChildType in TNodeIDToNodeType<
        keyof Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey]
      >]: TAddGridSetChildAction<NodeType, ChildKey, ChildType>;
    }[TNodeIDToNodeType<
      keyof Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey]
    >];
  }[TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "set">];
}[TNodeType];

type TActionUnion = TAddListChildActionUnion | TAddGridSetChildActionUnion;

export function addGridMapChild<
  NodeType extends TNodeType,
  ChildKey extends TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "grid_map">,
  SubKey extends keyof Extract<
    TNodeUnion,
    { type: NodeType }
  >["children"][ChildKey],
  ChildType extends TNodeIDToNodeType<
    Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey][SubKey]
  >
>(
  scene: TScene,
  id: TNodeID<NodeType>,
  childKey: ChildKey,
  subKey: SubKey,
  childType: ChildType,
  childId: TNodeID<ChildType> = createNodeId(),
  childProps: TNodeProps<ChildType> = defaultProps(childType)
) {
  return upd(scene, "nodes", (v) =>
    upd(v, id, (v) =>
      upd(v, "children", (v) => upd(v, childKey, (v) => ({ ...v })))
    )
  );
}

export function addOptionalChild<
  NodeType extends TNodeType,
  ChildKey extends TKeysOfValue<(typeof CHILD_TYPES)[NodeType], "optional">,
  // ChildKey extends keyof (typeof CHILD_TYPES)[NodeType],
  ChildType extends TNodeIDToNodeType<
    Extract<TNodeUnion, { type: NodeType }>["children"][ChildKey]
  >
>(
  scene: TScene,
  id: TNodeID<NodeType>,
  childKey: ChildKey,
  childType: ChildType,
  childId: TNodeID<ChildType> = createNodeId(),
  childProps: TNodeProps<ChildType> = defaultProps(childType)
) {
  return upd(scene, "nodes", (v) =>
    upd(v, id, (v) => upd(v, "children", (v) => set(v, childKey, v)))
  );
}

export function setProp<
  NodeType extends TNodeType,
  Prop extends keyof Extract<TNodeUnion, { type: NodeType }>["props"]
>(
  scene: TScene,
  id: TNodeID<NodeType>,
  prop: Prop,
  value: Extract<TNodeUnion, { type: NodeType }>["props"][Prop]
) {
  return upd(scene, "nodes", (v) =>
    upd(v, id, (v) => upd(v, "props", (v) => set(v, prop, value)))
  );
}

export function updateScene(scene: TScene, action: TActionUnion) {
  console.log(action);
  switch (action.action) {
    case "add_list_child":
      return addListChild(
        scene,
        action.id,
        action.childKey,
        action.position,
        action.childType,
        action.newIds
      );
    case "add_grid_set_child":
      return addGridSetChild(
        scene,
        action.id,
        action.childKey,
        action.position,
        action.childType,
        action.newIds
      );
  }
}

enum ServerOpcode {
  ReqSync = -1, // starting_from_version
  RelayTo = -2, // member_id, message
  LoadFile = -3,
}

enum ClientOpcode {
  SendSyncTo = -1, // member_id, starting_from_version
  StartLoadFile = -2,
  EndLoadFile = -3,
  LoadFileRow = -4,
}

function deserialize_surface(bytes: DataView): TSurfaceNode {
  let i = 18;
  const fixed_prop = bytes.getBigUint64(i);
  i += 16;
  const vary_prop_len = bytes.getUint16(i);
  i += 2;
  const vary_prop = new TextDecoder().decode(
    // bytes.slice(i, i + vary_prop_len)
    new DataView(bytes.buffer, bytes.byteOffset + i, vary_prop_len)
  );
  i += vary_prop_len;

  return {
    type: "surface",
    children: {
      nodes: vary_prop,
      states: {},
      events: {
        click: undefined,
        pointer_down: undefined,
        pointer_up: undefined,
      },
      props: {
        flex_direction: fixed_prop,
      },
    },
  };
}

export function loadFile(ws: WebSocket) {
  const message = new DataView(new ArrayBuffer(8));
  message.setBigInt64(0, BigInt(ServerOpcode.LoadFile));

  ws.send(message);
}

export function receiveFileRow(scene: TScene, bytes: DataView) {
  const t = bytes.getUint16(0);
  const id = bytes.getBigInt64(2);

  // const id = new TextDecoder().decode(
  //   new DataView(bytes.buffer, bytes.byteOffset + 4, 16)
  // );
  const node = deserialize_surface(
    new DataView(bytes.buffer, bytes.byteOffset + 2 + 4)
  );

  return upd(scene, "nodes", (v) => set(v, id as TAnyNodeID, node));
}

export function requestSync(ws: WebSocket, fromVersion: bigint) {
  const message = new DataView(new ArrayBuffer(8 + 8));
  message.setBigInt64(0, BigInt(ServerOpcode.ReqSync));
  message.setBigInt64(8, fromVersion);

  ws.send(message);
}

export function commitAction(
  scene: TScene,
  ws: WebSocket,
  action: TSceneActionUnion
) {
  const actionStr = new TextEncoder().encode(JSON.stringify(action));

  const message = new DataView(new ArrayBuffer(8 + actionStr.byteLength));
  message.setBigInt64(0, scene.version + BigInt(1));
  new Uint8Array(message.buffer).set(actionStr, 8);

  console.log(
    "send",
    message,
    message.getBigInt64(0),
    new TextDecoder().decode(new Uint8Array(message.buffer))
  );

  ws.send(message.buffer);
}

type MessageLog = {
  rawMessage: Uint8Array;
  parsedAction: TSceneActionUnion;
}[];

export function receiveAction(
  scene: TScene,
  msgLog: MessageLog,
  reqVersion: bigint,
  action: TSceneActionUnion
) {
  return updateScene(scene, action);
  // switch (action.action) {
  //   case "add_list_child":
  //     const offset = msgLog
  //       .slice(Number(reqVersion))
  //       .reduce(
  //         (acc, msg) =>
  //           (acc += +(
  //             msg.parsedAction.action === "add_list_child" &&
  //             msg.parsedAction.position < action.position
  //           )),
  //         0
  //       );
  //     const adjustedAction = upd(action, "position", (v) => v + offset);
  //     return updateScene(scene, adjustedAction);
  // }
}

export function relayAction(
  ws: WebSocket,
  memberId: bigint,
  actionStr: Uint8Array
) {
  const message = new DataView(new ArrayBuffer(8 + 8 + actionStr.byteLength));
  message.setBigInt64(0, BigInt(ServerOpcode.RelayTo));
  message.setBigUint64(8, memberId);
  new Uint8Array(message.buffer).set(actionStr, 16);

  ws.send(message);
}

export function receiveMessage(
  scene: TScene,
  ws: WebSocket,
  msgLog: MessageLog,
  bytes: ArrayBuffer
) {
  // console.log(
  //   "recv",
  //   msg,
  //   msg.getBigInt64(0),
  //   new TextDecoder().decode(new Uint8Array(msg.buffer))
  // );

  const version = msg.getBigInt64(0);

  const opcode = Number(version);
  if (opcode === ClientOpcode.SendSyncTo) {
    const memberId = msg.getBigInt64(8);
    const startingFrom = msg.getBigInt64(16);
    for (const msg of msgLog.slice(Number(startingFrom))) {
      relayAction(ws, memberId, msg.rawMessage);
    }
    return;
  } else if (opcode === ClientOpcode.LoadFileRow) {
    receiveFileRow(scene, new DataView(bytes, 8));
  }

  if (scene.version < version - BigInt(1)) {
    requestSync(ws, scene.version + BigInt(1));

    setTimeout(() => {
      if (scene.version < version) {
        // reload entire file
      }
    }, 8000);

    return;
  }

  const reqVersion = msg.getBigInt64(8);

  // console.log(new TextDecoder().decode(new Uint8Array(msg.buffer, 16)));

  scene.version = version;
  receiveAction(
    scene,
    msgLog,
    reqVersion,
    JSON.parse(new TextDecoder().decode(new Uint8Array(msg.buffer, 16)))
  );
}

export function updateSceneAndCommit(
  scene: TScene,
  ws: WebSocket,
  action: TAddListChildActionUnion
) {
  commitAction(scene, ws, action);
  return updateScene(scene, action);
}
